// /career-metrics — careers source/sink economy dashboard (staff-only).
// Epic #550 Phase 1, issue #563.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { EconomyDashboardLayout } from '@/components/admin/economy-dashboard-layout';

// ── Types ─────────────────────────────────────────────────────────────────────

interface PerCareerRow {
  career_id: number;
  day: string;
  cash_earned: number;
  points_earned: number;
  xp_generated: number;
}

interface TotalsRow {
  cash_earned: number;
  points_earned: number;
  xp_generated: number;
}

interface MetricsResponse {
  ok: boolean;
  range: { since: number | null; until: number | null };
  per_career: PerCareerRow[];
  totals: TotalsRow;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Format cash with ¥ symbol per D-Currency-1. */
function formatMoney(value: number): string {
  return `¥${value.toLocaleString()}`;
}

function buildQueryString(since: string, until: string, userId: string): string {
  const params = new URLSearchParams();
  if (since.trim()) params.set('since', since.trim());
  if (until.trim()) params.set('until', until.trim());
  if (userId.trim()) params.set('userId', userId.trim());
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

// ── Component ─────────────────────────────────────────────────────────────────

export function CareerMetricsPage() {
  const [draftSince, setDraftSince] = useState('');
  const [draftUntil, setDraftUntil] = useState('');
  const [draftUserId, setDraftUserId] = useState('');
  const [appliedSince, setAppliedSince] = useState('');
  const [appliedUntil, setAppliedUntil] = useState('');
  const [appliedUserId, setAppliedUserId] = useState('');

  const { data, isLoading, isError } = useQuery<MetricsResponse>({
    queryKey: ['career-metrics', appliedSince, appliedUntil, appliedUserId],
    queryFn: async () => {
      const qs = buildQueryString(appliedSince, appliedUntil, appliedUserId);
      const res = await apiFetch<MetricsResponse>(`/admin/career-metrics${qs}`);
      if (!res.ok) throw new Error('Failed to load career metrics.');
      return res.data;
    },
  });

  function handleApply() {
    setAppliedSince(draftSince);
    setAppliedUntil(draftUntil);
    setAppliedUserId(draftUserId);
  }

  return (
    <EconomyDashboardLayout
      title="Career Metrics"
      description="Economy summary across all career work. Defaults to all-time; narrow by Unix timestamp range."
      since={draftSince}
      until={draftUntil}
      onSinceChange={setDraftSince}
      onUntilChange={setDraftUntil}
      userId={draftUserId}
      onUserIdChange={setDraftUserId}
      onApply={handleApply}
      isLoading={isLoading}
      isError={isError}
      errorMessage="Failed to load career metrics."
    >
      <p className="text-sm text-ink-low">
        Note: career points spent on convert (SUB-11) and AP per work tick (SUB-12) are not logged.
      </p>

      {data != null && (
        <>
          {/* Totals */}
          <section className="space-y-2">
            <h2 className="text-lg font-medium text-ink-high">Totals</h2>
            <div className="overflow-x-auto rounded-sm border border-border">
              <table className="w-full text-sm" data-testid="career-totals-table">
                <thead className="bg-muted text-left">
                  <tr>
                    <th className="px-3 py-2 font-medium text-ink-mid">Cash earned</th>
                    <th className="px-3 py-2 font-medium text-ink-mid">Career points earned</th>
                    <th className="px-3 py-2 font-medium text-ink-mid">XP generated</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="hover:bg-panel-elev">
                    <td className="px-3 py-2 font-mono text-xs tabular-nums">{formatMoney(data.totals.cash_earned)}</td>
                    <td className="px-3 py-2 tabular-nums">{data.totals.points_earned.toLocaleString()}</td>
                    <td className="px-3 py-2 tabular-nums">{data.totals.xp_generated.toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Per career */}
          {data.per_career.length > 0 ? (
            <section className="space-y-2">
              <h2 className="text-lg font-medium text-ink-high">Per career / day</h2>
              <div className="overflow-x-auto rounded-sm border border-border">
                <table className="w-full text-sm" data-testid="career-per-career-table">
                  <thead className="bg-muted text-left">
                    <tr>
                      <th className="px-3 py-2 font-medium text-ink-mid">Career ID</th>
                      <th className="px-3 py-2 font-medium text-ink-mid">Day</th>
                      <th className="px-3 py-2 font-medium text-ink-mid">Cash earned</th>
                      <th className="px-3 py-2 font-medium text-ink-mid">Career points earned</th>
                      <th className="px-3 py-2 font-medium text-ink-mid">XP generated</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-divider">
                    {data.per_career.map((row, idx) => (
                      <tr key={idx} className="hover:bg-panel-elev">
                        <td className="px-3 py-2 font-mono text-xs">{row.career_id}</td>
                        <td className="px-3 py-2 text-ink-low">{row.day}</td>
                        <td className="px-3 py-2 font-mono text-xs tabular-nums">{formatMoney(row.cash_earned)}</td>
                        <td className="px-3 py-2 tabular-nums">{row.points_earned.toLocaleString()}</td>
                        <td className="px-3 py-2 tabular-nums">{row.xp_generated.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : (
            <p className="text-sm text-ink-low">No per-career data available.</p>
          )}
        </>
      )}
    </EconomyDashboardLayout>
  );
}
